/**
 * Sidebar Configuration Hook
 *
 * Unified sidebar configuration that can be used across different pages.
 * Supports both standard navigation (landing, profile, projects) and project-specific
 * mode switching (Uncover/Prototyper).
 *
 * This is platform-agnostic and can be used in any page facade.
 *
 * Note: Icons are passed as Lucide icon components. The UI layer should wrap them
 * with the Icon component for consistent styling.
 *
 * @module useSidebarConfig
 * @category Facades/Shared
 */

import { useMemo } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CirclePlus,
  Folders,
  FileText,
  Layers,
  Library,
  Lightbulb,
  MessageSquare,
  Package,
  Plus,
  Sparkles,
  MessageCircle,
} from 'lucide-react';
import type { ProjectMode } from '../../../types/project.types';

// Re-export ProjectMode for convenience
export type { ProjectMode };

export type SidebarPageType = 'standard' | 'project' | 'conversation' | 'scope';

export interface SidebarConfig {
  navigationItems: any[]; // NavigationItem[] from @ideoz/ui
  socialLinks: any[]; // SocialLink[] from @ideoz/ui
  /** Callback to navigate to landing (e.g. for logo click). */
  onNavigateToLanding: () => void;
  /** Page context (lets the template pick sensible defaults — e.g. scope opens expanded). */
  pageType: SidebarPageType;
}

export interface SidebarConfigParams {
  /**
   * Type of page: 'standard' for landing/profile/projects, 'project' for project details
   */
  pageType: SidebarPageType;

  /**
   * Whether the user is logged in
   */
  isLoggedIn: boolean;

  /**
   * Callback to reset conversation state
   */
  onResetConversation: () => void;

  /**
   * Callback to navigate to landing page
   */
  onNavigateToLanding: () => void;
  onNavigateToProjectsList?: () => void;
  onHomeClick?: () => void;

  /**
   * Current page path (optional, for highlighting active menu item in standard pages)
   */
  currentPath?: string;

  /**
   * Current active mode for the sidebar mode buttons.
   */
  currentMode?: ProjectMode;

  /**
   * Callback to change mode.
   */
  onModeChange: (mode: ProjectMode) => void;

  /**
   * Whether this is a project-based conversation (has projectId)
   */
  hasProject?: boolean;

  /**
   * Whether prototyper was accessed from onboarding (tmp conversation)
   */
  isOnboardingPrototyper?: boolean;

  /**
   * Scope page only: list of conversations for the active mode + handlers
   */
  scopeConversations?: Array<{ id: string; title: string }>;
  /** True while the recent-chats list is loading — renders shimmer rows. */
  isLoadingScopeConversations?: boolean;
  scopeActiveConversationId?: string | null;
  onSelectScopeConversation?: (conversationId: string) => void;
  onCreateScopeConversation?: () => void;
  isCreatingScopeConversation?: boolean;
  /** Scope page only: request deletion of a recent conversation (opens a confirm). */
  onDeleteScopeConversation?: (conversationId: string) => void;
  /** Scope page only: opens the UploadFiles dialog from the sidebar Files item. */
  onOpenScopeFiles?: () => void;
  /** Scope page only: navigates to the scope-level Artifacts page. */
  onOpenScopeArtifacts?: () => void;
  /**
   * Hide the Files + Artifacts items entirely (not just disable them). Used by
   * the Discussion Room, which reuses the scope sidebar but has neither.
   */
  hideScopeFilesArtifacts?: boolean;
}

// ========== Shared Utilities ==========

/**
 * Create Facebook Icon Element
 */
const createFacebookIcon = () =>
  React.createElement(
    'svg',
    {
      width: 24,
      height: 24,
      viewBox: '0 0 24 24',
      fill: '#1877F2',
    },
    React.createElement('path', {
      d: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
    })
  );

/**
 * Get social links configuration (shared across all pages)
 */
const getSocialLinks = () => [
  {
    id: 'facebook',
    label: 'Join Facebook group',
    url: 'https://www.facebook.com/groups/2315933918852784',
    icon: createFacebookIcon(),
    testId: 'social-facebook',
  },
];

// ========== Unified Sidebar Configuration Hook ==========

/**
 * Sidebar Configuration Hook
 *
 * Unified hook that handles both standard navigation and project mode navigation.
 * Use pageType='standard' for landing/profile/projects pages.
 * Use pageType='project' for project details pages with mode switching.
 */
export function useSidebarConfig({
  pageType,
  isLoggedIn,
  onResetConversation,
  onNavigateToLanding,
  onNavigateToProjectsList,
  currentPath,
  currentMode,
  onModeChange,
  onHomeClick,
  hasProject = false,
  isOnboardingPrototyper = false,
  scopeConversations,
  isLoadingScopeConversations = false,
  scopeActiveConversationId,
  onSelectScopeConversation,
  onCreateScopeConversation,
  isCreatingScopeConversation = false,
  onDeleteScopeConversation,
  onOpenScopeFiles,
  onOpenScopeArtifacts,
  hideScopeFilesArtifacts = false,
}: SidebarConfigParams): SidebarConfig {
  const { t } = useTranslation();
  const navigationItems = useMemo(() => {
    if (pageType === 'project') {
      // Project mode navigation (Uncover/Prototyper)
      if (!currentMode) {
        throw new Error('currentMode is required for project page type');
      }

      // Show prototyper only if:
      // 1. User has a project (normal project-based conversation), OR
      // 2. User accessed prototyper from onboarding (tmp conversation with prototyper)
      const showPrototyper = hasProject || isOnboardingPrototyper;

      const items = [
        {
          id: 'clarify',
          label: t('convNav.clarify'),
          icon: Sparkles,
          onClick: () => onModeChange!('clarify'),
          type: 'mode',
          testId: 'sidemenu-uncover',
          isActive: currentMode === 'clarify',
        },
      ];

      if (showPrototyper) {
        items.push({
          id: 'prototyper',
          label: t('convNav.prototyper'),
          icon: Lightbulb,
          onClick: () => onModeChange!('prototyper'),
          type: 'mode',
          testId: 'sidemenu-prototyper',
          isActive: currentMode === 'prototyper',
        });
      }

      return items;
    }
    if (pageType === 'conversation') {
      if (!currentMode) {
        throw new Error('currentMode is required for project page type');
      }

      // Show prototyper only if:
      // 1. User has a project (normal project-based conversation), OR
      // 2. User accessed prototyper from onboarding (tmp conversation with prototyper)
      const showHomeIcon = !hasProject || isOnboardingPrototyper;
      let items = [
        {
          id: 'clarify',
          label: t('convNav.clarify'),
          icon: Sparkles,
          onClick: () => onModeChange!('clarify'),
          type: 'mode',
          testId: 'sidemenu-uncover',
          isActive: currentMode === 'clarify',
        },
      ];

      items.push({
        id: 'prototyper',
        label: t('convNav.prototyper'),
        icon: Lightbulb,
        onClick: () => onModeChange!('prototyper'),
        type: 'mode',
        testId: 'sidemenu-prototyper',
        isActive: currentMode === 'prototyper',
      });
      if (showHomeIcon && onHomeClick) {
        items = [
          {
            id: 'new-chat',
            label: t('convNav.new'),
            icon: CirclePlus,
            onClick: () => {
              onHomeClick();
            },
            isActive: true,
            type: 'mode',
            testId: 'sidemenu-home',
          },
        ];
      }

      return items;
    }

    if (pageType === 'scope') {
      // Scope sidebar order: New chat, Files, Artifacts, then Recent chats.
      // The Strategy / Clarify mode pills were removed — the AI auto-selects the
      // mode now, so the FE no longer exposes a mode switcher here. (Prototyper
      // likewise isn't in this list; it's triggered from the composer.)
      const items: any[] = [];

      if (onCreateScopeConversation) {
        items.push({
          id: 'new-scope-conversation',
          label: t('convNav.new'),
          icon: CirclePlus,
          type: 'navigation',
          testId: 'sidemenu-new-scope-conversation',
          isActive: false,
          onClick: () => onCreateScopeConversation(),
        });
      }

      // The Discussion Room reuses this sidebar but has no Files/Artifacts, so
      // it omits these two items entirely (rather than showing them disabled).
      if (!hideScopeFilesArtifacts) {
        items.push(
          {
            id: 'files',
            label: t('convNav.files'),
            icon: FileText,
            type: 'navigation',
            disabled: !onOpenScopeFiles,
            testId: 'sidemenu-files',
            isActive: false,
            onClick: onOpenScopeFiles,
          },
          {
            id: 'artifacts',
            label: t('convNav.artifacts'),
            icon: Package,
            type: 'navigation',
            disabled: !onOpenScopeArtifacts,
            testId: 'sidemenu-artifacts',
            isActive: false,
            onClick: onOpenScopeArtifacts,
          }
        );
      }

      items.push({
        id: 'recent-header',
        label: t('convNav.recentConversations'),
        type: 'header',
        testId: 'sidemenu-recent-header',
      });

      const chats = scopeConversations ?? [];
      if (isLoadingScopeConversations && chats.length === 0) {
        // Shimmer placeholder rows while the recent-chats list loads.
        for (let i = 0; i < 4; i += 1) {
          items.push({
            id: `recent-skeleton-${i}`,
            label: '',
            type: 'skeleton',
            disabled: true,
            testId: `sidemenu-recent-skeleton-${i}`,
            isActive: false,
          });
        }
      } else if (chats.length === 0) {
        items.push({
          id: 'no-recent-chats',
          label: t('convNav.noRecentChats'),
          type: 'navigation',
          disabled: true,
          testId: 'sidemenu-no-recent-chats',
          isActive: false,
        });
      } else {
        chats.forEach((conv, idx) => {
          items.push({
            id: `conv-${conv.id}`,
            label: conv.title || t('convNav.chatFallback', { n: idx + 1 }),
            // icon: MessageCircle,
            type: 'navigation',
            testId: `sidemenu-conv-${conv.id}`,
            isActive: conv.id === scopeActiveConversationId,
            onClick: () => onSelectScopeConversation?.(conv.id),
            onDelete: onDeleteScopeConversation
              ? () => onDeleteScopeConversation(conv.id)
              : undefined,
            deleteLabel: t('convNav.deleteConversation'),
          });
        });
      }

      return items;
    }

    // Standard navigation (New Chat, My Projects, Community)
    // Only set isActive if currentPath is defined; otherwise let SideMenu handle path matching
    return [
      {
        id: 'new-chat',
        label: t('convNav.new'),
        icon: CirclePlus,
        onClick: () => {
          onResetConversation();
          onNavigateToLanding();
        },
        path: '/',
        type: 'navigation',
        testId: 'sidemenu-new-chat',
        isActive:
          currentPath !== undefined ? currentPath === '/' || currentPath === '/landing' : undefined,
      },
      {
        onClick: () => {
          onNavigateToProjectsList && onNavigateToProjectsList();
        },
        id: 'my-projects',
        label: t('convNav.myProjects'),
        icon: Layers,
        path: '/projects',
        type: 'navigation',
        testId: 'sidemenu-my-projects',
        isActive: currentPath !== undefined ? currentPath === '/projects' : undefined,
      },
    ];
  }, [
    t,
    pageType,
    isLoggedIn,
    onResetConversation,
    onNavigateToLanding,
    onNavigateToProjectsList,
    currentPath,
    currentMode,
    onModeChange,
    hasProject,
    isOnboardingPrototyper,
    scopeConversations,
    isLoadingScopeConversations,
    scopeActiveConversationId,
    onSelectScopeConversation,
    onCreateScopeConversation,
    isCreatingScopeConversation,
    onDeleteScopeConversation,
    onOpenScopeFiles,
    onOpenScopeArtifacts,
    hideScopeFilesArtifacts,
  ]);

  const socialLinks = useMemo(() => getSocialLinks(), []);

  return {
    navigationItems,
    socialLinks,
    onNavigateToLanding,
    pageType,
  };
}

// ========== Backwards Compatibility Exports ==========

/**
 * @deprecated Use useSidebarConfig with pageType='project' instead
 */
export const useSidebarProjectConfig = (params: Omit<SidebarConfigParams, 'pageType'>) =>
  useSidebarConfig({ ...params, pageType: 'project' });
